import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

let scriptPromise = null;

// load the turnstile script only once for the whole app
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Failed to load turnstile script"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Cloudflare Turnstile widget.
 * Tokens are single use, so call ref.reset() after a failed submit to get a new one.
 */
const TurnstileWidget = forwardRef(({ action, onToken, onExpire }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const handlersRef = useRef({ onToken, onExpire });
  handlersRef.current = { onToken, onExpire };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        handlersRef.current.onExpire?.();
      }
    }
  }));

  useEffect(() => {
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          callback: (token) => handlersRef.current.onToken?.(token),
          "expired-callback": () => handlersRef.current.onExpire?.(),
          "error-callback": () => handlersRef.current.onExpire?.(),
        });
      })
      .catch((err) => console.log("turnstile load error", err));

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [action]);

  return <div ref={containerRef} />;
});

export default TurnstileWidget;
